import React, { useState, useEffect, useCallback } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { SkeletonBlock, SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import {
    getMyMfaDevices,
    deleteMyMfaDevice,
    resetMyPassword,
    resetAllMyMfa,
    enrollMyMfa,
    type MfaDevice,
} from "./request/profileApi";

const MFA_TYPE_LABELS: Record<string, string> = {
    totp: "Authenticator App",
    sms: "SMS",
    email: "Email",
    "push-notification": "Push Notification",
    "webauthn-roaming": "Security Key",
    "webauthn-platform": "Biometrics",
    "recovery-code": "Recovery Code",
};

const SecurityTab: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const userProfile = useAppSelector((state) => state.userProfile);
    const isSsoUser = userProfile.id?.startsWith("samlp|");
    const canResetPassword = !isSsoUser;

    const [devices, setDevices] = useState<MfaDevice[]>([]);
    const [loading, setLoading] = useState(!isSsoUser);
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);
    const [confirmAction, setConfirmAction] = useState<{
        type: "deleteDevice" | "resetAll" | "resetPassword";
        deviceId?: string;
        deviceName?: string;
    } | null>(null);

    const loadDevices = useCallback(async () => {
        if (isSsoUser) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const response = await getMyMfaDevices(token);
            setDevices(response.data.devices || []);
        } catch {
            setFeedback({ type: "error", message: "Error loading MFA devices." });
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, isSsoUser]);

    useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    const handleDeleteDevice = async (methodId: string) => {
        try {
            const token = await getAccessTokenSilently();
            await deleteMyMfaDevice(token, methodId);
            setFeedback({ type: "success", message: "MFA device removed successfully." });
            setConfirmAction(null);
            loadDevices();
        } catch {
            setFeedback({ type: "error", message: "Error removing MFA device." });
            setConfirmAction(null);
        }
    };

    const handleResetAllMfa = async () => {
        try {
            const token = await getAccessTokenSilently();
            await resetAllMyMfa(token);
            setFeedback({ type: "success", message: "All MFA devices reset successfully." });
            setConfirmAction(null);
            loadDevices();
        } catch {
            setFeedback({ type: "error", message: "Error resetting MFA." });
            setConfirmAction(null);
        }
    };

    const handleResetPassword = async () => {
        try {
            const token = await getAccessTokenSilently();
            await resetMyPassword(token);
            setFeedback({
                type: "success",
                message: "Password reset email sent. Check your inbox.",
            });
            setConfirmAction(null);
        } catch {
            setFeedback({ type: "error", message: "Error sending password reset email." });
            setConfirmAction(null);
        }
    };

    const handleEnrollMfa = async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await enrollMyMfa(token);
            const { ticket_url } = response.data;
            window.open(ticket_url, "_blank", "noopener,noreferrer");
            setFeedback({
                type: "success",
                message: "MFA enrollment page opened in a new tab. Complete the setup there, then refresh this page.",
            });
        } catch {
            setFeedback({ type: "error", message: "Error creating MFA enrollment. Try again." });
        }
    };

    const handleConfirm = () => {
        if (!confirmAction) return;
        switch (confirmAction.type) {
            case "deleteDevice":
                if (confirmAction.deviceId) handleDeleteDevice(confirmAction.deviceId);
                break;
            case "resetAll":
                handleResetAllMfa();
                break;
            case "resetPassword":
                handleResetPassword();
                break;
        }
    };

    const getConfirmMessage = (): string => {
        if (!confirmAction) return "";
        switch (confirmAction.type) {
            case "deleteDevice":
                return `Are you sure you want to remove "${confirmAction.deviceName || "this device"}"? You may need to re-enroll this MFA method.`;
            case "resetAll":
                return "Are you sure you want to reset ALL MFA devices? You will need to re-enroll in MFA on your next login.";
            case "resetPassword":
                return "A password reset link will be sent to your email address. Are you sure?";
        }
    };

    return (
        <div className="space-y-8">
            {feedback && (
                <div
                    className={`px-4 py-3 rounded-md text-sm ${
                        feedback.type === "success"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                >
                    {feedback.message}
                </div>
            )}

            {/* Password Section */}
            <div>
                <h3 className="text-base font-semibold text-semantic-text-primary mb-2">
                    Password
                </h3>
                {canResetPassword ? (
                    <>
                        <p className="text-sm text-semantic-text-secondary mb-3">
                            Request a password reset link sent to your email.
                        </p>
                        <GenericButton
                            label="Reset Password"
                            variant="secondary"
                            onClick={() =>
                                setConfirmAction({ type: "resetPassword" })
                            }
                        />
                    </>
                ) : (
                    <div className="flex items-start gap-3 p-4 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-semantic-text-secondary flex-shrink-0 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                            />
                        </svg>
                        <p className="text-sm text-semantic-text-secondary">
                            Your password is managed by your organization&apos;s identity provider (SSO).
                            Contact your IT administrator to change your password.
                        </p>
                    </div>
                )}
            </div>

            {/* MFA Devices Section */}
            <div>
                <h3 className="text-base font-semibold text-semantic-text-primary mb-2">
                    MFA Devices
                </h3>
                {isSsoUser ? (
                    <div className="flex items-start gap-3 p-4 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-semantic-text-secondary flex-shrink-0 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                            />
                        </svg>
                        <p className="text-sm text-semantic-text-secondary">
                            Multi-factor authentication for SSO users is managed by your
                            organization&apos;s identity provider. Contact your IT administrator
                            to configure MFA.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-semantic-text-secondary">
                                Manage your multi-factor authentication devices.
                            </p>
                            {devices.length > 0 && (
                                <div className="flex gap-2">
                                    <GenericButton
                                        label="Enroll New Device"
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleEnrollMfa}
                                    />
                                    <GenericButton
                                        label="Reset All"
                                        variant="danger"
                                        size="sm"
                                        onClick={() => setConfirmAction({ type: "resetAll" })}
                                    />
                                </div>
                            )}
                        </div>

                        {loading ? (
                            <div className="space-y-3" aria-busy aria-label="Loading MFA devices">
                                {[0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between p-4 border border-semantic-border-secondary rounded-md"
                                    >
                                        <div className="space-y-2 flex-1">
                                            <SkeletonLine w="40%" h={14} />
                                            <SkeletonLine w="25%" h={11} />
                                        </div>
                                        <SkeletonBlock h={32} w={72} roundedClass="rounded-md" />
                                    </div>
                                ))}
                            </div>
                        ) : devices.length === 0 ? (
                            <div className="text-center py-8 border border-dashed border-semantic-border-secondary rounded-md">
                                <p className="text-semantic-text-secondary text-sm mb-4">
                                    No MFA devices enrolled.
                                </p>
                                <GenericButton
                                    label="Enroll MFA Device"
                                    variant="brand"
                                    size="sm"
                                    onClick={handleEnrollMfa}
                                />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {devices.map((device) => (
                                    <div
                                        key={device.id}
                                        className="flex items-center justify-between p-4 border border-semantic-border-secondary rounded-md bg-semantic-bg-primary"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-semantic-text-primary">
                                                        {MFA_TYPE_LABELS[device.type] || device.type}
                                                    </span>
                                                    <GenericTag
                                                        label="Active"
                                                        variant="success"
                                                        size="small"
                                                    />
                                                </div>
                                                {device.name && (
                                                    <span className="text-xs text-semantic-text-secondary">
                                                        {device.name}
                                                    </span>
                                                )}
                                                <span className="text-xs text-semantic-text-secondary block">
                                                    Added{" "}
                                                    {new Date(device.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        <GenericButton
                                            label="Remove"
                                            variant="danger"
                                            size="sm"
                                            onClick={() =>
                                                setConfirmAction({
                                                    type: "deleteDevice",
                                                    deviceId: device.id,
                                                    deviceName:
                                                        MFA_TYPE_LABELS[device.type] || device.type,
                                                })
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Confirmation Alert */}
            <GenericAlert
                isOpen={confirmAction !== null}
                onClose={() => setConfirmAction(null)}
                variant="warning"
                title="Confirm Action"
                description={getConfirmMessage()}
                onConfirm={handleConfirm}
                confirmLabel="Confirm"
                cancelLabel="Cancel"
            />
        </div>
    );
};

export default SecurityTab;
